import { useEffect, useMemo, useRef } from "react";
import { useTranslation } from "react-i18next";
import useEvent from "react-use-event-hook";
import {
  useRecoilState,
  useRecoilValue,
  useResetRecoilState,
  useSetRecoilState,
} from "recoil";
import { Subscription, tap, timer } from "rxjs";

import { usePageContext } from "@ryde/redux";
import {
  DEFAULT_LIVE_MAP_GRID_REFRESH_INTERVAL_SEC,
  DEFAULT_LIVE_MAP_MAP_REFRESH_INTERVAL_SEC,
  LIVE_MAP_MAP_POLL_PHASE_OFFSET_RATIO,
  liveMapAutofitPulseAtom,
  liveMapAutofitSuspendedAtom,
  liveMapEffectiveDisplayModeSelector,
  liveMapGridToolbarStatsAtomFamily,
  liveMapPollingUiAtom,
  liveMapRefreshGridIntervalSecOverrideAtom,
  liveMapRefreshMapIntervalSecOverrideAtom,
  Log,
  MILLISECONDS_IN_SECOND,
  nowTDateISO,
  resolveLiveMapRefreshIntervalSec,
  scheduleAfterPaint,
  selectedLiveMapMapRidesSF,
  showOnlyRidesWithNewIncidentsAtomFamily,
  sortAscendingNumbers,
  useSystemParams,
  useTimezone,
} from "@ryde/shared";

import { useLiveMapFetch } from "./use-live-map-fetch";
import { isLiveMapRequestCanceled } from "./use-live-map-fetch.utils";

export interface ILiveMapContext {
  initGridPolling?: () => void;
  /** Starts periodic tracking fetch for each ride id (same set as on the map). */
  initMapPolling?: (rideIds: readonly number[]) => void;
  stopGridPolling?: () => void;
  stopMapPolling?: () => void;
  fetchRidesWithLoader?: () => void;
  /** One-shot refresh: grid rides; plus map trackings/incidents when rides are selected on the map. */
  refreshGridAndMapSelection?: () => void;
  /** One-shot refresh: map trackings/incidents for selected rides only (triggers autofit when done). */
  refreshMapSelection?: () => void;
  /** Active grid polling interval in seconds (session override → system param → default). */
  gridRefreshIntervalSec?: number;
  /** Active map polling interval in seconds (session override → system param → default). */
  mapRefreshIntervalSec?: number;
}

interface IInitGridPollingOptions {
  /** After bulk select-all: resume timer without immediate rides/search poll. */
  skipImmediatePollTick?: boolean;
}

export const useLiveMapCtxProvider = (): ILiveMapContext => {
  const { entity } = usePageContext();
  const {
    enableIncidents,
    liveMapRefreshGridIntervalSec,
    liveMapRefreshMapIntervalSec,
  } = useSystemParams();
  const gridRefreshIntervalSecOverride = useRecoilValue(
    liveMapRefreshGridIntervalSecOverrideAtom,
  );
  const mapRefreshIntervalSecOverride = useRecoilValue(
    liveMapRefreshMapIntervalSecOverrideAtom,
  );
  const mapRides = useRecoilValue(selectedLiveMapMapRidesSF(entity));
  const liveMapDisplayMode = useRecoilValue(
    liveMapEffectiveDisplayModeSelector,
  );
  const bulkAutofitSuspended = useRecoilValue(liveMapAutofitSuspendedAtom);
  // Grid polling replaces the whole rides array on every tick (setRides), so `mapRides`
  // gets a fresh reference each poll. Returning a new array here would re-run the map
  // polling effect below every grid tick, tearing down and recreating the RxJS map timer
  // before it can fire (both timers share the same period) — the selected ride's car then
  // never updates from the recurring poll and appears frozen until refresh/reselect.
  // Keep the previous reference when the selected ride-id set is unchanged so the map poll
  // survives across grid polls.
  const mapRideIdsSortedRef = useRef<readonly number[]>([]);
  const mapRideIdsSorted = useMemo(() => {
    const ids = sortAscendingNumbers(
      new Set(
        mapRides
          .map(r => r.id)
          .filter((id): id is number => typeof id === "number" && id > 0),
      ),
    );

    const prev = mapRideIdsSortedRef.current;
    if (prev.length === ids.length && prev.every((id, i) => id === ids[i])) {
      return prev;
    }

    mapRideIdsSortedRef.current = ids;
    return ids;
  }, [mapRides]);
  const { t } = useTranslation();
  const { timezone } = useTimezone();

  const setAutofitPulse = useSetRecoilState(liveMapAutofitPulseAtom);
  const [liveUi, setLiveUi] = useRecoilState(liveMapPollingUiAtom);
  const resetLiveUi = useResetRecoilState(liveMapPollingUiAtom);
  const resetShowOnlyNewIncidentsFilter = useResetRecoilState(
    showOnlyRidesWithNewIncidentsAtomFamily(entity),
  );
  const resetLiveMapGridToolbarStats = useResetRecoilState(
    liveMapGridToolbarStatsAtomFamily(entity),
  );

  useEffect(() => {
    if (!enableIncidents) {
      resetShowOnlyNewIncidentsFilter();
    }
  }, [enableIncidents, resetShowOnlyNewIncidentsFilter]);

  const gridRefreshIntervalSec = useMemo(
    () =>
      resolveLiveMapRefreshIntervalSec({
        overrideValue: gridRefreshIntervalSecOverride,
        systemParamValue: liveMapRefreshGridIntervalSec,
        fallback: DEFAULT_LIVE_MAP_GRID_REFRESH_INTERVAL_SEC,
      }),
    [gridRefreshIntervalSecOverride, liveMapRefreshGridIntervalSec],
  );
  const mapRefreshIntervalSec = useMemo(
    () =>
      resolveLiveMapRefreshIntervalSec({
        overrideValue: mapRefreshIntervalSecOverride,
        systemParamValue: liveMapRefreshMapIntervalSec,
        fallback: DEFAULT_LIVE_MAP_MAP_REFRESH_INTERVAL_SEC,
      }),
    [mapRefreshIntervalSecOverride, liveMapRefreshMapIntervalSec],
  );

  const gridPollingInterval = gridRefreshIntervalSec * MILLISECONDS_IN_SECOND;
  const mapPollingInterval = mapRefreshIntervalSec * MILLISECONDS_IN_SECOND;
  const mapPollPhaseOffsetMs = Math.floor(
    mapPollingInterval * LIVE_MAP_MAP_POLL_PHASE_OFFSET_RATIO,
  );

  const subscriptionGridRef = useRef<Subscription | null>(null);
  const subscriptionMapRef = useRef<Subscription | null>(null);
  const gridPollFetchDepthRef = useRef(0);
  const mapPollFetchDepthRef = useRef(0);
  const prevGridRefreshIntervalSecForGridPollRef = useRef(
    gridRefreshIntervalSec,
  );
  const bulkAutofitSuspendedRef = useRef(bulkAutofitSuspended);
  const gridPollingPausedForBulkRef = useRef(false);

  useEffect(() => {
    bulkAutofitSuspendedRef.current = bulkAutofitSuspended;
  }, [bulkAutofitSuspended]);

  const {
    abortGridPollInFlight,
    abortMapPollInFlight,
    backgroundFetchRides,
    backgroundFetchTrackingsBatch,
    backgroundFetchLiveMapIncidentsBatch,
    fetchRidesInitial,
    fetchTrackingsInitialBatch,
    fetchLiveMapIncidentsInitialBatch,
  } = useLiveMapFetch();

  const beginGridPollFetch = useEvent(() => {
    gridPollFetchDepthRef.current += 1;
    if (gridPollFetchDepthRef.current === 1) {
      setLiveUi(s => ({ ...s, isGridPollFetchInFlight: true }));
    }
  });

  const endGridPollFetch = useEvent(() => {
    gridPollFetchDepthRef.current = Math.max(
      0,
      gridPollFetchDepthRef.current - 1,
    );
    if (gridPollFetchDepthRef.current === 0) {
      setLiveUi(s => ({ ...s, isGridPollFetchInFlight: false }));
    }
  });

  const beginMapPollFetch = useEvent(() => {
    mapPollFetchDepthRef.current += 1;
    if (mapPollFetchDepthRef.current === 1) {
      setLiveUi(s => ({ ...s, isMapPollFetchInFlight: true }));
    }
  });

  const endMapPollFetch = useEvent(() => {
    mapPollFetchDepthRef.current = Math.max(
      0,
      mapPollFetchDepthRef.current - 1,
    );
    if (mapPollFetchDepthRef.current === 0) {
      setLiveUi(s => ({ ...s, isMapPollFetchInFlight: false }));
    }
  });

  const stopGridPolling = useEvent(() => {
    if (!subscriptionGridRef.current?.closed) {
      Log.debug(`Stop/pause/cleanup polling grid`);
      subscriptionGridRef.current?.unsubscribe();
    }

    abortGridPollInFlight();
    subscriptionGridRef.current = null;
    setLiveUi(s => ({
      ...s,
      isGridPollingRunning: false,
      isGridPollFetchInFlight: false,
    }));
    gridPollFetchDepthRef.current = 0;
  });

  const stopMapPolling = useEvent(() => {
    if (!subscriptionMapRef.current?.closed) {
      Log.debug(`Stop/pause/cleanup polling map`);
      subscriptionMapRef.current?.unsubscribe();
    }

    abortMapPollInFlight();
    subscriptionMapRef.current = null;
    setLiveUi(s => ({
      ...s,
      mapPollingRideIds: undefined,
      isMapPollFetchInFlight: false,
    }));
    mapPollFetchDepthRef.current = 0;
  });

  const initGridPolling = useEvent((options?: IInitGridPollingOptions) => {
    Log.debug("Init grid polling");
    stopGridPolling();
    setLiveUi(s => ({ ...s, isGridPollingRunning: true }));
    const firstInterval = options?.skipImmediatePollTick
      ? gridPollingInterval
      : 0;
    // First tick at 0 so the status pill updates immediately (timer(d, d) waits d ms before first emit).
    subscriptionGridRef.current = timer(firstInterval, gridPollingInterval)
      .pipe(
        tap(pollingCount => {
          if (bulkAutofitSuspendedRef.current) {
            return;
          }

          // RxJS timer emits 0,1,2…; surface count as 1-based in the UI.
          setLiveUi(s => ({
            ...s,
            gridPoolingCount: pollingCount + 1,
            lastSynchroGridDate: nowTDateISO(timezone, false),
          }));
          beginGridPollFetch();
          void backgroundFetchRides?.().finally(() => {
            endGridPollFetch();
          });
          Log.debug(`Polling table, attempt ${pollingCount}`);
        }),
      )
      .subscribe();
  });

  const fetchRidesWithLoader = useEvent(async () => {
    stopGridPolling();

    try {
      const res = await fetchRidesInitial();

      if (res) {
        initGridPolling?.();
      } else {
        stopGridPolling?.();
        stopMapPolling?.();
      }
    } catch (e) {
      stopGridPolling?.();
      stopMapPolling?.();
      Log.warnAndToast(t("errors.errorFetchingRides"), e);
    }
  });

  // Grid RxJS timer keeps the interval from the last initGridPolling(); refresh when interval changes.
  useEffect(() => {
    if (
      prevGridRefreshIntervalSecForGridPollRef.current ===
      gridRefreshIntervalSec
    ) {
      return;
    }

    prevGridRefreshIntervalSecForGridPollRef.current = gridRefreshIntervalSec;

    if (!liveUi.isGridPollingRunning) {
      return;
    }

    initGridPolling({ skipImmediatePollTick: true });
  }, [gridRefreshIntervalSec, liveUi.isGridPollingRunning, initGridPolling]);

  const refreshMapSelection = useEvent(() => {
    if (bulkAutofitSuspendedRef.current) {
      return;
    }

    const ids = mapRideIdsSorted;
    if (!ids.length) {
      return;
    }

    const ts = nowTDateISO(timezone, false);
    setLiveUi(s => ({
      ...s,
      mapPoolingCount: s.mapPoolingCount + 1,
      lastSynchroMapDate: ts,
    }));

    beginMapPollFetch();
    void Promise.all([
      backgroundFetchTrackingsBatch?.(ids) ?? Promise.resolve(),
      backgroundFetchLiveMapIncidentsBatch?.(ids) ?? Promise.resolve(),
    ]).finally(() => {
      endMapPollFetch();
      scheduleAfterPaint(() => {
        queueMicrotask(() => {
          setAutofitPulse(count => count + 1);
        });
      });
    });
  });

  const refreshGridAndMapSelection = useEvent(() => {
    if (bulkAutofitSuspendedRef.current) {
      return;
    }

    const ids = mapRideIdsSorted;
    const ts = nowTDateISO(timezone, false);

    if (!ids.length) {
      setLiveUi(s => ({
        ...s,
        gridPoolingCount: s.gridPoolingCount + 1,
        lastSynchroGridDate: ts,
      }));
      beginGridPollFetch();
      void (backgroundFetchRides?.() ?? Promise.resolve()).finally(() => {
        endGridPollFetch();
      });
      return;
    }

    setLiveUi(s => ({
      ...s,
      gridPoolingCount: s.gridPoolingCount + 1,
      mapPoolingCount: s.mapPoolingCount + 1,
      lastSynchroGridDate: ts,
      lastSynchroMapDate: ts,
    }));

    beginGridPollFetch();
    beginMapPollFetch();

    void Promise.all([
      backgroundFetchRides?.() ?? Promise.resolve(),
      backgroundFetchTrackingsBatch?.(ids) ?? Promise.resolve(),
      backgroundFetchLiveMapIncidentsBatch?.(ids) ?? Promise.resolve(),
    ]).finally(() => {
      endGridPollFetch();
      endMapPollFetch();
    });
  });

  const initMapPolling = useEvent((rideIds: readonly number[]) => {
    if (bulkAutofitSuspendedRef.current) {
      return;
    }

    const ids = [...new Set(rideIds)].filter(id => id > 0);
    Log.debug(`Init MAP polling for ${ids.length} rides`);
    stopMapPolling();

    if (!ids.length) {
      return;
    }

    void fetchTrackingsInitialBatch(ids)
      .then(initialTrackingsResult => {
        if (isLiveMapRequestCanceled(initialTrackingsResult)) {
          return;
        }

        if (initialTrackingsResult?.success !== true) {
          return;
        }

        if (bulkAutofitSuspendedRef.current) {
          return;
        }

        void fetchLiveMapIncidentsInitialBatch(ids);
      })
      .catch(e => {
        Log.warn("Failed fetching tracking data", e);
        Log.warnAndToast(t("errors.failedFetchingTrackingData"), []);
      });

    setLiveUi(s => ({
      ...s,
      mapPollingRideIds: ids,
      mapPoolingCount: 1,
      lastSynchroMapDate: nowTDateISO(timezone, false),
    }));

    subscriptionMapRef.current = timer(mapPollPhaseOffsetMs, mapPollingInterval)
      .pipe(
        tap(pollingCount => {
          if (bulkAutofitSuspendedRef.current) {
            return;
          }

          setLiveUi(s => ({
            ...s,
            mapPoolingCount: s.mapPoolingCount + 1,
            lastSynchroMapDate: nowTDateISO(timezone, false),
          }));
          beginMapPollFetch();
          void Promise.all([
            backgroundFetchTrackingsBatch?.(ids) ?? Promise.resolve(),
            backgroundFetchLiveMapIncidentsBatch?.(ids) ?? Promise.resolve(),
          ]).finally(() => {
            endMapPollFetch();
          });
          Log.debug(
            `Polling map for rides ${ids.join(", ")}, attempt ${pollingCount + 1}`,
          );
        }),
      )
      .subscribe();
  });

  // Pause grid/map polling while bulk selection defers map render (avoid races with Recoil updates).
  useEffect(() => {
    if (!bulkAutofitSuspended) {
      if (gridPollingPausedForBulkRef.current) {
        gridPollingPausedForBulkRef.current = false;
        if (liveUi.isGridPollingRunning) {
          initGridPolling({ skipImmediatePollTick: true });
        }
      }

      return;
    }

    abortGridPollInFlight();
    abortMapPollInFlight();

    if (
      liveUi.isGridPollingRunning &&
      subscriptionGridRef.current &&
      !subscriptionGridRef.current.closed
    ) {
      gridPollingPausedForBulkRef.current = true;
      subscriptionGridRef.current.unsubscribe();
      subscriptionGridRef.current = null;
      setLiveUi(s => ({ ...s, isGridPollFetchInFlight: false }));
      gridPollFetchDepthRef.current = 0;
    }

    stopMapPolling();
  }, [
    bulkAutofitSuspended,
    liveUi.isGridPollingRunning,
    initGridPolling,
    abortGridPollInFlight,
    abortMapPollInFlight,
    stopMapPolling,
    setLiveUi,
  ]);

  // Map tracking poll follows the same ride set as the map (checkboxes or single focused row).
  useEffect(() => {
    if (bulkAutofitSuspended) {
      return;
    }

    if (!mapRideIdsSorted.length) {
      stopMapPolling();
      return;
    }

    void initMapPolling(mapRideIdsSorted);

    return () => {
      stopMapPolling();
    };
  }, [
    mapRideIdsSorted,
    liveMapDisplayMode,
    bulkAutofitSuspended,
    stopMapPolling,
    initMapPolling,
    mapRefreshIntervalSec,
  ]);

  // Stop polling and reset shared UI atom when leaving the live map page (Recoil survives unmount).
  useEffect(() => {
    return () => {
      stopGridPolling();
      stopMapPolling();
      resetLiveUi();
      resetShowOnlyNewIncidentsFilter();
      resetLiveMapGridToolbarStats();
    };
  }, [
    stopGridPolling,
    stopMapPolling,
    resetLiveUi,
    resetShowOnlyNewIncidentsFilter,
    resetLiveMapGridToolbarStats,
  ]);

  return useMemo(
    (): ILiveMapContext => ({
      initGridPolling,
      initMapPolling,
      stopGridPolling,
      stopMapPolling,
      refreshGridAndMapSelection,
      refreshMapSelection,
      fetchRidesWithLoader,
      gridRefreshIntervalSec,
      mapRefreshIntervalSec,
    }),
    [
      initGridPolling,
      initMapPolling,
      stopGridPolling,
      stopMapPolling,
      refreshGridAndMapSelection,
      refreshMapSelection,
      fetchRidesWithLoader,
      gridRefreshIntervalSec,
      mapRefreshIntervalSec,
    ],
  );
};
